import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { from, of } from 'rxjs';
import { first, map, switchMap, catchError, withLatestFrom, mapTo } from 'rxjs/operators';
import { DeviceDetailService, Inverter } from 'ngx-core-services';

import {
  AppState,
  SystemsState,
  getSystemDefinitionConfiguration,
  requestSystemDeviceDetails,
  selectedSystemMetadata,
  setSystemDeviceDetailsError,
  updateSystemDeviceDetails,
  } from '..';

@Injectable()
export class DeviceDetailEffects {

  requestDeviceDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(requestSystemDeviceDetails),
      switchMap((action) =>
        this.store.select(selectedSystemMetadata).pipe(
          first(),
          mapTo(action),
        ),
      ),
      withLatestFrom(this.store),
      switchMap(([action, { systemState }]) =>
        from(this.getDeviceDetail(action.systemId, action.deviceId, action.forceRefresh, systemState)).pipe(
          map((deviceDetails) => {
            if (! deviceDetails ) {
              return setSystemDeviceDetailsError({ systemId: action.systemId, instanceId: action.instanceId, error: 'No telemetry for this device' });
            }
            return updateSystemDeviceDetails({ systemId: action.systemId, instanceId: action.instanceId, deviceId: action.deviceId, deviceDetails });
          }),
          catchError(_ => of(setSystemDeviceDetailsError({ systemId: action.systemId, instanceId: action.instanceId, error: 'Failed to fetch system power state' })))
        )
      )
    )
  );

  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private readonly deviceDetailService: DeviceDetailService,
  ) {}

  async getDeviceDetail(systemId: string, deviceId: string, forceRefresh: boolean, state: SystemsState): Promise<Partial<Inverter> | undefined> {
    const system = state.systemList[systemId];
    if (!system) {
      throw new Error(`System with id ${systemId} not found`);
    }

    const systemConfiguration = getSystemDefinitionConfiguration(system);

    const details = await this.deviceDetailService.fetchDeviceFwInfo(deviceId, systemConfiguration.monitoringType, forceRefresh);

    return details ? details : {};
  }
}
